import { type FC, type HTMLAttributes, type Ref, useEffect } from "react";
import { cn } from "../../lib/utils";
import { ClickableIcon, ClickableText, Icon, Text } from "../../primitives";
import { trackingAttrs } from "../../utils";
import {
	PANEL_TOAST_DURATION_MS,
	PANEL_TOAST_ERROR_DURATION_MS,
	type PanelToastData,
	type PanelToastTone,
} from "./usePanelToast";

const TONE_STYLES = {
	success: {
		role: undefined,
		container: "bg-surface-darker",
		icon: "check-circle",
		iconColor: "inverse",
		text: "text-inverse",
		durationMs: PANEL_TOAST_DURATION_MS,
	},
	danger: {
		role: "alert",
		container: "border border-intent-danger-border bg-intent-danger-bg",
		icon: "exclamation-circle",
		iconColor: "danger",
		text: "text-intent-danger-text",
		durationMs: PANEL_TOAST_ERROR_DURATION_MS,
	},
} as const satisfies Record<PanelToastTone, unknown>;

export type PanelToastProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
	ref?: Ref<HTMLDivElement>;
	/** The toast to show, or null for none. A new `id` replaces it and restarts the timer. */
	toast: PanelToastData | null;
	/** Fires with the toast id once it has been up for `durationMs`, or is closed. */
	onDismiss: (id: number) => void;
	/** @default 5000, or 10000 for a `danger` toast. A toast with an action stays until closed. */
	durationMs?: number;
	trackingId?: string;
	testId?: string;
};

/**
 * One toast for a docked panel, pinned 12px in from the bottom and sides of the nearest
 * `relative` ancestor. A confirmation is a dark pill with a check; a `danger` toast reports a
 * failure in the danger palette and stays up longer. Either may carry one action. Pair with
 * `usePanelToast`, which keeps it to one toast at a time. The live region stays mounted while
 * empty so the first message is announced.
 */
export const PanelToast: FC<PanelToastProps> = ({
	toast,
	onDismiss,
	durationMs,
	className,
	trackingId,
	testId = "panel-toast",
	ref,
	...rest
}) => {
	const toastId = toast?.id;
	const tone = toast?.tone ?? "success";
	const styles = TONE_STYLES[tone];
	const timeout = durationMs ?? styles.durationMs;
	/** Its action may be the only way back to lost work, so it waits to be used or closed. */
	const persistent = toast?.action !== undefined;

	useEffect(() => {
		if (toastId === undefined || persistent) return;
		const timer = setTimeout(() => onDismiss(toastId), timeout);
		return () => clearTimeout(timer);
	}, [toastId, timeout, persistent, onDismiss]);

	return (
		<div
			ref={ref}
			role="status"
			aria-live="polite"
			aria-atomic
			className={cn(
				"pointer-events-none absolute inset-x-3 bottom-3 z-40 flex justify-start",
				className,
			)}
			data-testid={testId}
			{...trackingAttrs(trackingId)}
			{...rest}
		>
			{toast && (
				<div
					key={toast.id}
					role={styles.role}
					data-tone={tone}
					className={cn(
						"pointer-events-auto flex max-w-full animate-panel-toast-rise items-center gap-2 rounded-md px-3 py-2 shadow-panel-menu",
						styles.container,
					)}
				>
					<Icon type={styles.icon} size="mini" color={styles.iconColor} aria-hidden />
					<Text type="body" className={cn("min-w-0", styles.text)}>
						{toast.message}
					</Text>
					{toast.action && (
						<ClickableText
							type="body-strong"
							className={cn("ml-1 shrink-0", styles.text)}
							testId={`${testId}-action`}
							onClick={() => {
								toast.action?.onClick();
								onDismiss(toast.id);
							}}
						>
							{toast.action.label}
						</ClickableText>
					)}
					{persistent && (
						<ClickableIcon
							aria-label="Dismiss"
							type="x-mark"
							size="mini"
							color={styles.iconColor}
							className="shrink-0"
							testId={`${testId}-dismiss`}
							onClick={() => onDismiss(toast.id)}
						/>
					)}
				</div>
			)}
		</div>
	);
};
PanelToast.displayName = "PanelToast";
